import * as THREE from 'three'
import {
  isFeatureFace,
  isFeatureLine,
  isFeaturePoint,
  isFeaturePreviewMesh,
  isFeatureResultMesh,
  //------ 模型显示模式 开始------
  FeatureTopologyDisplayEdgesMesh,
  //------ 模型显示模式 结束------
} from '../../mesh/feature'
import {
  resolveImportedModelTopologyRepository,
} from '../../imported-model/topology/ImportedModelTopologyRepository'
import type { ImportedMeshTopologyBinding } from '../../imported-model/topology/ImportedMeshTopologyBinding'
import { ImportedModelObjectMetadataService } from '../../imported-model/metadata'
import { createImportedBodyOccurrenceBindingId } from '../../imported-model/utils'
import { isDatumAxis } from '../../reference/DatumAxis'
import {
  isDatumPlaneMesh,
  isDatumPlaneObject,
} from '../../reference/DatumPlane'
import { isDatumPoint } from '../../reference/DatumPoint'
import {
  isRuntimeImportedBodyEntryLabel,
  parseImportedBodyEntries,
} from '../../imported-model/loaders/body-entries'
//------ 装配体引用复制保存 开始------
import {
  AssemblyReferenceCopyLineageMetadataService,
  AssemblyReferenceCopyLineageRole,
  AssemblyReferenceCopyMetadataService,
  type AssemblyReferenceCopyLineageMetadata,
  type AssemblyReferenceCopyMetadata,
} from '../../assembly/reference-copy'
//------ 装配体引用复制保存 结束------
import type { AssemblyMateDocumentJSON } from '../../assembly/document'
import type { Matrix4Tuple } from '../../imported-model/loaders/types'
import {
  readVisibleExportSourceHierarchy,
  resolveVisibleExportSourceRoots,
  resolveVisibleExportSourceAssemblyNodeId,
  shouldBindVisibleExportMeshToParentAssemblyNode,
  shouldFlattenVisibleExportAssemblyLayer,
} from './visibleModelExportHierarchyPolicy'
import { toMatrixTuple } from '../../imported-model/loaders/assembly-node-utils'
import type {
  PuppyAssemblyNode,
  PuppyDisplayNodeBinding,
  PuppyDisplayNodeBodyBinding,
  PuppyDisplayPrimitiveFaceRange,
  PuppyDatumExtensionInput,
  PuppyVisibleBodyTopologyAsset,
  PuppyVisibleModelExportDiagnostics,
  PuppyVisibleModelExportRoot,
  PuppyExportAssemblySemanticSource,
  PuppyExportResultBodyDisplayNameMap,
  PuppyPackageDocumentType,
} from './types'
import { createPuppyAssemblyDocument } from './createPuppyAssemblyDocument'
import {
  createDisplaySnapshotGroup,
  createDisplaySnapshotMesh,
  normalizeDisplayNodeName,
} from './displaySnapshotObjects'
import {
  createDisplayNodeBinding,
  createInitialDiagnostics,
} from './visibleModelExportUtils'
import { createPuppyBodyTopologyAssetKey } from './bodyTopologyAssetKey'
import { cloneAssemblyNodeMeta } from './assemblyNodeMeta'
import { applyPuppyAssemblySemanticSource } from './applyPuppyAssemblySemanticSource'
import { remapPuppyVisibleModelExportRootMateBodyBindingReferences } from './remapPuppyAssemblyMateDocument'
import { finalizePuppyAssemblyPersistence } from './finalizePuppyAssemblyPersistence'
import { allocateStableDisplayNodeId } from './stableDisplayNodeId'
import {
  isCadObjectRuntimeContainer,
  isCadObjectRuntimeDerived,
} from '../../document/CadObjectPersistence'
import { importedModelSourceMeshExportBridge } from '../../imported-model/instancing/suppression'

/**
 * 读取 record 中的可选字符串。
 *
 * @param record 记录对象。
 * @param keys 候选 key。
 * @returns 第一个非空字符串。
 */
function readFirstString(record: Record<string, unknown> | undefined, keys: string[]): string | undefined {
  if (!record) {
    return undefined
  }

  for (const key of keys) {
    const value = record[key]
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim()
    }
  }

  return undefined
}

/**
 * 将未知值收窄为普通对象。
 *
 * @param value 待收窄的值。
 * @returns 普通对象；非对象值返回 `undefined`。
 */
function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object'
    ? value as Record<string, unknown>
    : undefined
}

/**
 * 读取 Object3D 相对父节点的本地矩阵。
 *
 * @param object 待读取的 three.js 对象。
 * @returns 可写入 assembly.json 的 4x4 矩阵元组。
 */
function readObjectLocalTransform(object: THREE.Object3D) {
  object.updateMatrix()
  return toMatrixTuple(object.matrix)
}

/**
 * 记录可见导出源对象的本地矩阵。
 *
 * @param context 导出构建上下文。
 * @param object 当前源对象。
 */
function recordSourceObjectLocalTransform(
  context: PuppyVisibleModelBuildContext,
  object: THREE.Object3D,
): void {
  const transform = readObjectLocalTransform(object)
  const stableNodeId = resolveStableSourceAssemblyNodeId(object)
  const sourceObjectIds = [object.uuid, stableNodeId]
  sourceObjectIds.forEach((objectId) => {
    if (objectId && !context.sourceObjectLocalTransformById.has(objectId)) {
      context.sourceObjectLocalTransformById.set(objectId, transform)
    }
  })
}

/**
 * 判断对象及其祖先链是否可见。
 *
 * @param object 场景对象。
 * @returns 是否处于可见链路。
 */
function isWorldVisible(object: THREE.Object3D): boolean {
  let current: THREE.Object3D | null = object
  while (current) {
    if (!current.visible) {
      return false
    }
    current = current.parent
  }
  return true
}

/**
 * 判断 mesh 是否具备可导出的顶点数据。
 *
 * @param mesh 候选 mesh。
 * @returns 是否具备有效顶点数据。
 */
function hasRenderableGeometry(mesh: THREE.Mesh): boolean {
  const position = mesh.geometry?.getAttribute('position')
  return Boolean(position && position.count > 0)
}

/**
 * 判断材质是否至少存在一个可见通道。
 *
 * @param material mesh 材质。
 * @returns 是否至少有一个可见材质。
 */
function hasVisibleMaterial(material: THREE.Material | THREE.Material[]): boolean {
  const materials = Array.isArray(material) ? material : [material]
  return materials.some((item) => item.visible !== false)
}

/**
 * 读取 Object3D 顶层布尔标记。
 *
 * @param object 场景对象。
 * @param key 顶层标记名称。
 * @returns 标记是否被显式置为 true。
 */
function readTopLevelBooleanFlag(object: THREE.Object3D, key: string): boolean {
  return (object as unknown as Record<string, unknown>)[key] === true
}

/**
 * 判断对象是否为 three.js 辅助显示对象。
 *
 * @param object 场景对象。
 * @returns 是否为辅助对象。
 */
function isThreeHelperObject(object: THREE.Object3D): boolean {
  if (
    object instanceof THREE.GridHelper
    || object instanceof THREE.AxesHelper
    || object instanceof THREE.BoxHelper
    || object instanceof THREE.Box3Helper
    || object instanceof THREE.PlaneHelper
    || object instanceof THREE.PolarGridHelper
    || object instanceof THREE.ArrowHelper
  ) {
    return true
  }

  return false
}

//------ 离线包基准扩展 开始------
/**
 * 判断对象是否为由基准模块管理的参考显示对象。
 *
 * 设计说明：
 * 1. 基准点、基准轴、基准面是 CAD 文档级参考几何，已经由 `assembly.json.extensions.datums` 保存；
 * 2. 它们的 Three.js 显示对象不对应 Worker BRep Shape，也不需要 topology-runtime；
 * 3. 因此可见模型导出必须跳过这些显示对象，避免把基准面的面片误判为缺少 BRep 的实体网格。
 *
 * @param object 场景对象。
 * @returns 是否为基准参考显示对象。
 */
function isDatumReferenceDisplayObject(object: THREE.Object3D): boolean {
  return isDatumPoint(object)
    || isDatumAxis(object)
    || isDatumPlaneObject(object)
    || isDatumPlaneMesh(object)
}
//------ 离线包基准扩展 结束------

/**
 * 判断对象及其子树是否应整体排除在可见模型导出之外。
 *
 * @param object 场景对象。
 * @returns 是否跳过整棵子树。
 */
function shouldSkipVisibleExportSubtree(object: THREE.Object3D): boolean {
  if (
    readTopLevelBooleanFlag(object, 'ignore')
    //------ 线性零部件阵列离线包定义持久化 开始------
    || isCadObjectRuntimeDerived(object)
    //------ 线性零部件阵列离线包定义持久化 结束------
    || isThreeHelperObject(object)
    || isDatumReferenceDisplayObject(object)
  ) {
    return true
  }

  if (
    isFeatureFace(object)
    || isFeatureLine(object)
    || isFeaturePoint(object)
    || isFeaturePreviewMesh(object)
    //------ 模型显示模式 开始------
    || object instanceof FeatureTopologyDisplayEdgesMesh
    //------ 模型显示模式 结束------
  ) {
    return true
  }

  const userData = asRecord(object.userData)
  return userData?.isPreview === true
    || userData?.isSketchHelper === true
    || userData?.isDatumHelper === true
    || userData?.skipSelectionSubtree === true
    || userData?.isSelectionHighlight === true
    || userData?.isImportedModelOverlay === true
    || userData?.isImportedModelOverlayChannel === true
    || userData?.isGlbDatumSlotOverlay === true
}

/**
 * 判断对象自身是否应排除，但子级仍需继续按原层级参与导出。
 *
 * @param object 场景对象。
 * @returns 是否只跳过当前对象。
 */
function shouldSkipVisibleExportObjectOnly(object: THREE.Object3D): boolean {
  const userData = asRecord(object.userData)
  return isCadObjectRuntimeContainer(object)
    || readTopLevelBooleanFlag(object, 'skipSelection')
    || userData?.skipSelection === true
}

/**
 * 计算 BufferGeometry group 对应的 raycast faceIndex 范围。
 *
 * @param group BufferGeometry group。
 * @returns faceIndex 范围。
 */
function createFaceRangeFromGeometryGroup(group: THREE.BufferGeometry['groups'][number]): {
  startFaceIndex: number
  endFaceIndex: number
} {
  const startFaceIndex = Math.max(0, Math.floor(group.start / 3))
  const triangleCount = Math.max(0, Math.floor(group.count / 3))
  return {
    startFaceIndex,
    endFaceIndex: startFaceIndex + Math.max(0, triangleCount - 1),
  }
}

/**
 * 计算 mesh 全部几何对应的 raycast faceIndex 范围。
 *
 * @param mesh 目标 mesh。
 * @returns faceIndex 范围。
 */
function createFullMeshFaceRange(mesh: THREE.Mesh): {
  startFaceIndex: number
  endFaceIndex: number
} {
  const indexCount = mesh.geometry.index?.count
  const positionCount = mesh.geometry.getAttribute('position')?.count ?? 0
  const triangleCount = Math.max(0, Math.floor((indexCount ?? positionCount) / 3))
  return {
    startFaceIndex: 0,
    endFaceIndex: Math.max(0, triangleCount - 1),
  }
}

/**
 * 将 primitive ID 列表映射到当前 mesh 的 faceIndex 范围。
 *
 * @param mesh 目标 mesh。
 * @param primitiveIds primitive ID 列表。
 * @param primitiveIndices 当前 Mesh 绑定的 primitive 下标列表。
 * @returns primitive face 范围列表。
 */
function createPrimitiveFaceRanges(
  mesh: THREE.Mesh,
  primitiveIds: readonly string[],
  primitiveIndices: readonly number[] = primitiveIds.map((_primitiveId, index) => index),
): PuppyDisplayPrimitiveFaceRange[] {
  const groups = mesh.geometry.groups ?? []
  const groupRanges = groups.map(createFaceRangeFromGeometryGroup)
  const fullRange = createFullMeshFaceRange(mesh)

  return primitiveIds.map((primitiveId, index) => ({
    primitiveId,
    ...(groupRanges[primitiveIndices[index] ?? index] ?? fullRange),
  }))
}

/**
 * 为没有导入拓扑仓库的普通结果体创建默认 primitive 范围。
 *
 * @param mesh 目标 mesh。
 * @returns 默认 primitive face 范围。
 */
function createDefaultPrimitiveFaceRanges(mesh: THREE.Mesh): PuppyDisplayPrimitiveFaceRange[] {
  const groups = mesh.geometry.groups ?? []
  if (groups.length === 0) {
    return [{
      primitiveId: 'primitive:0',
      ...createFullMeshFaceRange(mesh),
    }]
  }

  return groups.map((group, index) => ({
    primitiveId: `primitive:${index}`,
    ...createFaceRangeFromGeometryGroup(group),
  }))
}

/**
 * 解析原生结果体写入离线包的用户可见名称。
 *
 * @param input 解析输入。
 * @returns 用户可见结果体名称。
 */
function resolveResultBodyDisplayName(input: {
  context: PuppyVisibleModelBuildContext
  objectId: string
  mesh: THREE.Mesh
}): string {
  const mappedName = input.context.resultBodyDisplayNameByObjectId.get(input.objectId)?.trim()
  if (mappedName) {
    return mappedName
  }

  if (isFeatureResultMesh(input.mesh)) {
    const metadataName = readFirstString(
      asRecord(input.mesh.getFeatureMetadata()),
      ['displayName', 'name'],
    )
    if (metadataName) {
      return metadataName
    }
  }

  const objectName = input.mesh.name?.trim()
  return objectName || input.objectId
}

/**
 * 从 FeatureResultMesh 读取 body 级 BRep 绑定。
 *
 * @param mesh 目标 mesh。
 * @param context 离线包导出上下文。
 * @returns body 绑定列表。
 */
function resolveFeatureResultBodyBindings(
  mesh: THREE.Mesh,
  context: PuppyVisibleModelBuildContext,
): PuppyDisplayNodeBodyBinding[] {
  if (!isFeatureResultMesh(mesh)) {
    return []
  }

  const objectId = mesh.objectId.trim()
  if (!objectId) {
    return []
  }

  const primitiveFaceRanges = createDefaultPrimitiveFaceRanges(mesh)
  const sourceFaceRanges = mesh.getFaceTriangleRanges()
  return [{
    geometryObjectId: objectId,
    bodyBindingId: objectId,
    bodyId: objectId,
    name: resolveResultBodyDisplayName({
      context,
      objectId,
      mesh,
    }),
    primitiveIds: primitiveFaceRanges.map((range) => range.primitiveId),
    primitiveIndices: primitiveFaceRanges.map((_range, index) => index),
    primitiveFaceRanges,
    // 原生结果体与 imported 结果采用相同持久化契约，保证首次保存和重载再保存内容稳定。
    topologyFaceTriangleRanges: mesh.getTopologySnapshot()?.faces.flatMap((face, ordinal) => {
      const triangleRange = sourceFaceRanges?.[ordinal] ?? face.triangleRange
      return triangleRange && triangleRange[1] > triangleRange[0]
        ? [{ faceIndex: face.index, faceRef: face.ref, subEntityId: face.subEntityId, triangleRange: [...triangleRange] as [number, number] }]
        : []
    }),
  }]
}

/**
 * 判断实体显示名是否可直接写入离线包协议。
 *
 * @param value 候选显示名。
 * @returns 是否为非技术 ID 的可读名称。
 */
function isReadableBodyDisplayName(value: string | null | undefined): value is string {
  if (!value || value.trim().length === 0) {
    return false
  }
  const normalizedValue = value.trim()
  return !isRuntimeImportedBodyEntryLabel({
    bodyId: normalizedValue,
    label: normalizedValue,
  })
}

/**
 * 从当前 visible mesh 的 bodyEntries 中恢复可读实体名称。
 *
 * @param mesh 当前导入可见 mesh。
 * @param binding body 拓扑绑定。
 * @returns 可读实体名；没有时返回 undefined。
 */
function resolveImportedBodyDisplayName(
  mesh: THREE.Mesh,
  binding: ImportedMeshTopologyBinding['bodyBinding'],
): string | undefined {
  const bodyEntries = parseImportedBodyEntries(mesh.userData?.importedPartBodyEntries)
  const candidateIds = [
    binding.bodyBindingId,
    binding.bodyId,
  ].filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
  const matchedEntry = bodyEntries.find((entry) => (
    candidateIds.some((candidateId) => (
      entry.bodyBindingId === candidateId || entry.bodyId === candidateId
    ))
  ))
  return isReadableBodyDisplayName(matchedEntry?.label)
    ? matchedEntry.label.trim()
    : undefined
}

/**
 * 读取已由 Worker 注册确认的导入体 BRep 几何 ID。
 *
 * topo sidecar 的 `meshData.metadata.objectId` 只标识交互拓扑，不能证明 Worker GeometryState
 * 持有同名 Shape。离线快照必须只消费 BRep 注册成功后写入的显式 `geometryObjectId`，否则应在
 * 构建绑定时立即终止，避免把不可导出的交互 ID 延迟到 Worker 阶段才暴露。
 *
 * 调用边界：只有声明了 `brepRef`、且确实参与离线包 BRep 导出的 body 才能调用本函数；
 * 合法 topo-only body 只提供交互拓扑，不属于 BRep 导出目标。
 *
 * @param record 声明了 BRep 持久化引用的导入体拓扑仓库记录。
 * @returns Worker GeometryState 中已注册的权威 BRep 几何 ID。
 * @throws 导入体尚未完成 BRep 注册时抛出包含 body 身份的诊断。
 */
function requireRegisteredImportedBrepGeometryObjectId(
  record: ImportedMeshTopologyBinding,
): string {
  const geometryObjectId = record.topologyAsset.geometryObjectId?.trim() ?? ''
  if (geometryObjectId) {
    return geometryObjectId
  }

  const bodyBindingId = record.bodyBinding.bodyBindingId.trim()
  const bodyId = record.bodyBinding.bodyId?.trim() || bodyBindingId
  throw new Error(
    `导入模型 body 的 BRep 未注册，无法建立 Worker 几何绑定：bodyBindingId=${bodyBindingId}，bodyId=${bodyId}`,
  )
}

/**
 * 从 imported-model 拓扑仓库读取 body 级 BRep 绑定。
 *
 * @param mesh 目标 mesh。
 * @param records 拓扑仓库已经按该可见 mesh 解析出的 body 记录。
 * @returns body 绑定列表。
 */
function resolveImportedBodyBindings(
  mesh: THREE.Mesh,
  records: readonly ImportedMeshTopologyBinding[],
): PuppyDisplayNodeBodyBinding[] {
  return records
    .map((record: ImportedMeshTopologyBinding): PuppyDisplayNodeBodyBinding | null => {
      const primitiveIds = record.bodyBinding.primitiveIds.filter((primitiveId) => primitiveId.trim().length > 0)
      if (primitiveIds.length === 0 || !record.bodyBinding.brepRef) {
        return null
      }
      const geometryObjectId = requireRegisteredImportedBrepGeometryObjectId(record)

      return {
        geometryObjectId,
        bodyBindingId: record.bodyBinding.bodyBindingId,
        bodyId: record.bodyBinding.bodyId ?? record.bodyBinding.bodyBindingId,
        name: resolveImportedBodyDisplayName(mesh, record.bodyBinding),
        primitiveIds,
        primitiveIndices: [...record.primitiveIndices],
        primitiveFaceRanges: createPrimitiveFaceRanges(mesh, primitiveIds, record.primitiveIndices),
        // 正式保存 Mesh/Body 局部契约，不能依赖 GLTFExporter 是否继续保留源 geometry extras。
        topologyFaceTriangleRanges: record.bodyBinding.topologyFaceTriangleRanges?.map(range => ({
          ...range, triangleRange: [...range.triangleRange] as [number, number],
        })),
      }
    })
    .filter((binding): binding is PuppyDisplayNodeBodyBinding => binding !== null)
}

/**
 * 从轻量 userData 读取 BRep 绑定。
 *
 * @param mesh 目标 mesh。
 * @returns body 绑定列表与是否存在歧义。
 */
function resolveUserDataBodyBindings(mesh: THREE.Mesh): {
  bodyBindings: PuppyDisplayNodeBodyBinding[]
  ambiguous: boolean
} {
  const userData = asRecord(mesh.userData)
  const puppy = asRecord(userData?.puppy)
  const importedSelectionTarget = asRecord(userData?.importedSelectionTarget)
  const metadata = asRecord(userData?.metadata)

  const singleIds = [
    readFirstString(puppy, ['geometryObjectId', 'objectId']),
    readFirstString(userData, ['geometryObjectId', 'objectId']),
    readFirstString(importedSelectionTarget, ['geometryObjectId', 'objectId']),
    readFirstString(metadata, ['geometryObjectId', 'objectId']),
  ]

  const arrayIds = Array.isArray(puppy?.geometryObjectIds)
    ? puppy.geometryObjectIds
    : []
  const geometryObjectIds = Array.from(new Set([
    ...singleIds,
    ...arrayIds,
  ].filter((objectId): objectId is string => typeof objectId === 'string' && objectId.trim().length > 0)))

  /**
   * 多 ID 但没有 body/primitive 映射时无法还原点线面命中语义。这里不猜测，
   * 交给导出校验阻止生成不完整离线包。
   */
  if (geometryObjectIds.length !== 1) {
    return {
      bodyBindings: [],
      ambiguous: geometryObjectIds.length > 1,
    }
  }

  const geometryObjectId = geometryObjectIds[0]!
  const primitiveFaceRanges = createDefaultPrimitiveFaceRanges(mesh)
  return {
    ambiguous: false,
    bodyBindings: [{
      geometryObjectId,
      bodyBindingId: geometryObjectId,
      bodyId: geometryObjectId,
      name: mesh.name || geometryObjectId,
      primitiveIds: primitiveFaceRanges.map((range) => range.primitiveId),
      primitiveIndices: primitiveFaceRanges.map((_range, index) => index),
      primitiveFaceRanges,
    }],
  }
}

/**
 * 解析 mesh 对应的 body 级拓扑绑定。
 *
 * @param mesh 目标 mesh。
 * @returns body 绑定列表与诊断信息。
 */
function resolveDisplayNodeBodyBindings(
  mesh: THREE.Mesh,
  context: PuppyVisibleModelBuildContext,
): {
  bodyBindings: PuppyDisplayNodeBodyBinding[]
  ambiguous: boolean
} {
  const featureBindings = resolveFeatureResultBodyBindings(mesh, context)
  if (featureBindings.length > 0) {
    return {
      bodyBindings: featureBindings,
      ambiguous: false,
    }
  }

  const importedRepository = resolveImportedModelTopologyRepository(mesh)
  const importedRecords = importedRepository?.resolveBodyRecordsByVisibleMesh(mesh) ?? []
  if (importedRecords.length > 0) {
    return {
      /**
       * 拓扑仓库一旦命中就是导入 body 身份的权威来源。即使记录全部为 topo-only，
       * 也不能继续回退到 mesh.userData 中的 pick/objectId，否则会再次把交互 ID 当成 Worker Shape。
       */
      bodyBindings: resolveImportedBodyBindings(mesh, importedRecords),
      ambiguous: false,
    }
  }

  return resolveUserDataBodyBindings(mesh)
}

/**
 * 读取 BufferAttribute 中的 xyz 数据。
 *
 * @param attribute 顶点属性。
 * @returns 展开后的 Float32Array。
 */
function readVector3Attribute(attribute: THREE.BufferAttribute | THREE.InterleavedBufferAttribute): Float32Array {
  const values = new Float32Array(attribute.count * 3)
  for (let index = 0; index < attribute.count; index += 1) {
    values[index * 3] = attribute.getX(index)
    values[index * 3 + 1] = attribute.getY(index)
    values[index * 3 + 2] = attribute.getZ(index)
  }
  return values
}

/**
 * 读取几何体索引。
 *
 * @param geometry 源几何体。
 * @returns Uint32 索引；无索引时返回 undefined。
 */
function readGeometryIndices(geometry: THREE.BufferGeometry): Uint32Array | undefined {
  const indexAttribute = geometry.index
  if (!indexAttribute || indexAttribute.count <= 0) {
    return undefined
  }

  const indices = new Uint32Array(indexAttribute.count)
  for (let index = 0; index < indexAttribute.count; index += 1) {
    indices[index] = indexAttribute.getX(index)
  }
  return indices
}

/**
 * 将 display primitive 的闭区间 faceIndex 转换为 MeshData 使用的开区间三角形范围。
 *
 * @param ranges display primitive face 范围。
 * @returns MeshData faceTriangleRanges。
 */
function createMeshDataFaceTriangleRanges(
  ranges: PuppyDisplayPrimitiveFaceRange[],
): [number, number][] {
  return ranges.map((range) => [
    range.startFaceIndex,
    range.endFaceIndex + 1,
  ])
}

/**
 * 从原生 FeatureResultMesh 创建可直接导入消费的拓扑资产。
 *
 * @param mesh 原生特征结果 mesh。
 * @param binding body 绑定。
 * @returns 拓扑资产；缺少拓扑快照时返回 null。
 */
function createFeatureResultTopologyAsset(
  mesh: THREE.Mesh,
  binding: PuppyDisplayNodeBodyBinding,
): PuppyVisibleBodyTopologyAsset | null {
  if (!isFeatureResultMesh(mesh)) {
    return null
  }

  const topologySnapshot = mesh.getTopologySnapshot()
  const positionAttribute = mesh.geometry.getAttribute('position')
  if (!topologySnapshot || !positionAttribute) {
    return null
  }

  const normalAttribute = mesh.geometry.getAttribute('normal')
  const positions = readVector3Attribute(positionAttribute)
  const normals = normalAttribute
    ? readVector3Attribute(normalAttribute)
    : new Float32Array(positions.length)
  const faceTriangleRanges = mesh.getFaceTriangleRanges()
    ?? createMeshDataFaceTriangleRanges(binding.primitiveFaceRanges)

  return {
    bodyBindingId: binding.bodyBindingId,
    geometryObjectId: binding.geometryObjectId,
    exportDefinitionId: binding.geometryObjectId,
    meshData: {
      id: binding.geometryObjectId,
      positions,
      normals,
      indices: readGeometryIndices(mesh.geometry),
      faceTriangleRanges,
      topologySnapshot,
      metadata: {
        ...mesh.getFeatureMetadata(),
        objectId: binding.geometryObjectId,
        bodyBindingId: binding.bodyBindingId,
        bodyId: binding.bodyId,
        source: 'puppy-topology-runtime',
        sourceLevel: 'body',
      },
    },
  }
}

/**
 * 从 imported-model 拓扑仓库读取 body 级拓扑资产。
 *
 * @param mesh 可见 imported mesh。
 * @returns bodyBindingId 到拓扑资产的索引。
 */
function createImportedTopologyAssetIndex(mesh: THREE.Mesh): Map<string, PuppyVisibleBodyTopologyAsset> {
  const repository = resolveImportedModelTopologyRepository(mesh)
  if (!repository) {
    return new Map()
  }

  const assets = repository.resolveBodyRecordsByVisibleMesh(mesh)
    .map((record): PuppyVisibleBodyTopologyAsset | null => {
      if (!record.bodyBinding.brepRef || !record.topologyAsset.meshData.topologySnapshot) {
        return null
      }
      const geometryObjectId = requireRegisteredImportedBrepGeometryObjectId(record)

      return {
        bodyBindingId: record.bodyBinding.bodyBindingId,
        geometryObjectId,
        exportDefinitionId: record.topologyAsset.exportDefinitionId ?? geometryObjectId,
        meshData: {
          ...record.topologyAsset.meshData,
          metadata: {
            ...(record.topologyAsset.meshData.metadata ?? {}),
            objectId: geometryObjectId,
            bodyBindingId: record.bodyBinding.bodyBindingId,
            bodyId: record.bodyBinding.bodyId,
            source: 'puppy-topology-runtime',
            sourceLevel: record.bodyBinding.sourceLevel,
          },
        },
      }
    })
    .filter((asset): asset is PuppyVisibleBodyTopologyAsset => asset !== null)

  return new Map(assets.map((asset) => [asset.bodyBindingId, asset]))
}

/**
 * 解析当前可见 mesh 的 body 级拓扑资产。
 *
 * @param mesh 目标 mesh。
 * @param bodyBindings body 绑定列表。
 * @returns 可导出的拓扑资产列表。
 */
function resolveVisibleBodyTopologyAssets(
  mesh: THREE.Mesh,
  bodyBindings: PuppyDisplayNodeBodyBinding[],
): PuppyVisibleBodyTopologyAsset[] {
  if (bodyBindings.length === 0) {
    return []
  }

  const importedAssets = createImportedTopologyAssetIndex(mesh)
  if (importedAssets.size > 0) {
    return bodyBindings
      .map((binding) => importedAssets.get(binding.bodyBindingId) ?? null)
      .filter((asset): asset is PuppyVisibleBodyTopologyAsset => asset !== null)
  }

  return bodyBindings
    .map((binding) => createFeatureResultTopologyAsset(mesh, binding))
    .filter((asset): asset is PuppyVisibleBodyTopologyAsset => asset !== null)
}

/**
 * 规整 GLB 节点名称。
 *
 * @param input 原始输入。
 * @returns 可写入 GLB 的短名称。
 */
/**
 * 创建初始诊断对象。
 *
 * @returns 诊断对象。
 */
/**
 * 可见模型离线包导出构建上下文。
 */
interface PuppyVisibleModelBuildContext {
  /** GLB 节点绑定。*/
  nodeBindings: PuppyDisplayNodeBinding[]
  /** body 拓扑资产索引。*/
  bodyTopologyAssetsByKey: Map<string, PuppyVisibleBodyTopologyAsset>
  /** 导出 bodyBindingId 到 BRep 几何对象 ID 的索引。*/
  /** 源 bodyBindingId、装配 occurrence 与 BRep 几何对象组合到导出 bodyBindingId 的索引。*/
  /** 原生结果体 ID 到用户可见名称的只读映射。 */
  resultBodyDisplayNameByObjectId: PuppyExportResultBodyDisplayNameMap
  /** 导出诊断。*/
  diagnostics: PuppyVisibleModelExportDiagnostics
  /** 装配节点列表。*/
  assemblyNodes: PuppyAssemblyNode[]
  /** 装配节点索引。*/
  assemblyNodesById: Map<string, PuppyAssemblyNode>
  /** 被折叠但仍需参与 Mate 节点引用重映射的语义节点。 */
  foldedAssemblyNodes: PuppyAssemblyNode[]
  /** 源 Object3D UUID 到本地矩阵的索引。 */
  sourceObjectLocalTransformById: Map<string, NonNullable<PuppyAssemblyNode['transform']>>
  /** 父节点到子节点 ID 的索引。*/
  childIdsByNodeId: Record<string, string[]>
  /** display node 自增序列。*/
  displayNodeSeq: number
  /** 当前导出事务已经占用的稳定 display node ID。 */
  displayNodeIds: Set<string>
  /** assembly node 自增序列。*/
  assemblyNodeSeq: number
  //------ 装配体引用复制族 开始------
  /** 已导出 display payload 的引用复制族 ID。 */
  exportedReferenceFamilyIds: Set<string>
  /** 当前可见源中仍然存在原始 occurrence 的引用复制族 ID。 */
  originReferenceFamilyIds: Set<string>
  //------ 装配体引用复制族 结束------
}

/**
 * 克隆递归结果。
 */
interface PuppyCloneTreeResult {
  /** 克隆后的 GLB 节点集合。*/
  clones: THREE.Object3D[]
  /** 当前子树暴露给 assembly.json 的根装配节点 ID 集合。*/
  rootAssemblyNodeIds: string[]
}

/**
 * 创建构建上下文。
 *
 * @returns 构建上下文。
 */
function createBuildContext(input: {
  /** 原生结果体 ID 到用户可见名称的只读映射。 */
  resultBodyDisplayNameByObjectId?: PuppyExportResultBodyDisplayNameMap | null
} = {}): PuppyVisibleModelBuildContext {
  return {
    nodeBindings: [],
    bodyTopologyAssetsByKey: new Map(),
    resultBodyDisplayNameByObjectId: input.resultBodyDisplayNameByObjectId ?? new Map<string, string>(),
    diagnostics: createInitialDiagnostics(),
    assemblyNodes: [],
    assemblyNodesById: new Map(),
    foldedAssemblyNodes: [],
    sourceObjectLocalTransformById: new Map(),
    childIdsByNodeId: {},
    displayNodeSeq: 0,
    displayNodeIds: new Set(),
    assemblyNodeSeq: 0,
    //------ 装配体引用复制族 开始------
    exportedReferenceFamilyIds: new Set(),
    originReferenceFamilyIds: new Set(),
    //------ 装配体引用复制族 结束------
  }
}

/**
 * 向数组追加唯一字符串。
 *
 * @param target 目标数组。
 * @param values 待追加值。
 */
function pushUniqueStrings(target: string[], values: string[]): void {
  values.forEach((value) => {
    if (value.trim().length > 0 && !target.includes(value)) {
      target.push(value)
    }
  })
}

//------ 装配体引用复制保存 开始------
/** 引用复制代表 payload 矩阵比较容差。 */
const REFERENCE_COPY_REPRESENTATIVE_MATRIX_TOLERANCE = 1e-5

/**
 * 读取导出用的引用复制元数据。
 *
 * 设计说明：
 * 复制体不导出 display mesh，扩展字段只保存“引用谁”的轻量身份。
 * 复制体“放在哪里”由装配节点自身的 transform 字段统一承载，避免两份矩阵发生漂移。
 *
 * @param object 导出源对象。
 * @returns 可序列化引用复制元数据；普通对象返回 null。
 */
function resolveAssemblyReferenceCopyExportMetadata(
  object: THREE.Object3D,
): AssemblyReferenceCopyMetadata | null {
  const metadata = AssemblyReferenceCopyMetadataService.readOwnMetadata(object)
  if (!metadata) {
    return null
  }

  return metadata
}

/**
 * 读取导出用的引用复制族元数据。
 *
 * @param object 导出源对象。
 * @returns 可序列化复制族元数据；普通对象返回 null。
 */
function resolveAssemblyReferenceCopyLineageExportMetadata(
  object: THREE.Object3D,
): AssemblyReferenceCopyLineageMetadata | null {
  return AssemblyReferenceCopyLineageMetadataService.readOwnMetadata(object)
    ?? AssemblyReferenceCopyLineageMetadataService.readMetadataFromSelfOrAncestors(object)
}

/**
 * 将矩阵元组转为 THREE.Matrix4。
 *
 * @param transform 装配节点矩阵元组。
 * @returns THREE 矩阵。
 */
function createMatrixFromTransformTuple(transform: Matrix4Tuple): THREE.Matrix4 {
  return new THREE.Matrix4().fromArray(transform)
}

/**
 * 比较两个矩阵是否近似相等。
 *
 * @param left 左侧矩阵。
 * @param right 右侧矩阵。
 * @returns 是否在容差范围内相等。
 */
function areReferenceCopyMatricesClose(left: THREE.Matrix4, right: THREE.Matrix4): boolean {
  return left.elements.every((value, index) => (
    Math.abs(value - right.elements[index]!) <= REFERENCE_COPY_REPRESENTATIVE_MATRIX_TOLERANCE
  ))
}

/**
 * 解析引用复制代表 payload 的子节点折叠矩阵。
 *
 * 设计说明：
 * 当原始 occurrence 已不存在时，第一个复制体会承担 display payload 代表职责。旧会话中
 * 复制体子节点可能仍保留 `copy^-1 * source` 的局部矩阵；导出时若继续写出该矩阵，
 * 重载后复制体会落回源位置。这里只在子节点矩阵明确等于旧偏移时折回，避免影响已经
 * 规范化的复制体和普通零件内部结构。
 *
 * @param input 解析输入。
 * @returns 需要传给子节点快照的折叠矩阵；无需折叠时返回 null。
 */
function resolveReferenceCopyRepresentativeChildFoldedMatrix(input: {
  context: PuppyVisibleModelBuildContext
  metadata: AssemblyReferenceCopyMetadata | null
  copiedObject: THREE.Object3D
  child: THREE.Object3D
}): THREE.Matrix4 | null {
  if (!input.metadata?.sourceNodeId) {
    return null
  }

  const sourceTransform = input.context.sourceObjectLocalTransformById.get(input.metadata.sourceNodeId)
  if (!sourceTransform) {
    return null
  }

  input.copiedObject.updateMatrix()
  input.child.updateMatrix()
  const sourceMatrix = createMatrixFromTransformTuple(sourceTransform)
  const copiedMatrix = input.copiedObject.matrix.clone()
  const legacyDelta = new THREE.Matrix4().copy(copiedMatrix).invert().multiply(sourceMatrix)
  if (!areReferenceCopyMatricesClose(input.child.matrix, legacyDelta)) {
    return null
  }

  return legacyDelta.invert()
}
//------ 装配体引用复制保存 结束------

//------ 装配体引用复制族 开始------
/**
 * 收集当前可见源中仍存在原始 occurrence 的复制族。
 *
 * @param sourceRoots 导出源对象集合。
 * @returns 原始 occurrence 复制族 ID 集合。
 */
function collectOriginReferenceFamilyIds(sourceRoots: THREE.Object3D[]): Set<string> {
  const originFamilyIds = new Set<string>()
  sourceRoots.forEach((sourceRoot) => {
    sourceRoot.traverse((object) => {
      const lineage = AssemblyReferenceCopyLineageMetadataService.readOwnMetadata(object)
      if (
        lineage?.role === AssemblyReferenceCopyLineageRole.OriginOccurrence
        && lineage.referenceFamilyId.trim().length > 0
      ) {
        originFamilyIds.add(lineage.referenceFamilyId)
      }
    })
  })
  return originFamilyIds
}
//------ 装配体引用复制族 结束------

/**
 * 解析装配节点名称。
 *
 * @param object 源对象。
 * @param fallbackIndex 兜底序号。
 * @returns 节点名称。
 */
function resolveAssemblyNodeName(
  object: THREE.Object3D,
  fallbackIndex: number,
  context?: PuppyVisibleModelBuildContext,
): string {
  const hierarchyName = readVisibleExportSourceHierarchy(object)?.name?.trim()
  if (!hierarchyName && context && object instanceof THREE.Mesh && isFeatureResultMesh(object)) {
    const objectId = object.objectId.trim()
    if (objectId) {
      return resolveResultBodyDisplayName({
        context,
        objectId,
        mesh: object,
      })
    }
  }
  const objectName = object.name?.trim()
  return hierarchyName || objectName || `${object.type}-${fallbackIndex}`
}

/**
 * 读取导出源对象自身的稳定装配节点 ID。
 *
 * 设计说明：
 * MateEntityRef 持久化依赖 `assemblyNodeId`。如果离线包导出时重新生成节点 ID，
 * 重载后 Mate 引用会无法通过 AssemblyTopologySelectionRef 恢复。
 * 因此这里优先复用 imported-model metadata 中的稳定节点 ID；仅缺失时回退，身份冲突必须报错。
 *
 * @param object 源对象。
 * @returns 稳定装配节点 ID；不存在时返回 `null`。
 */
function resolveStableSourceAssemblyNodeId(object: THREE.Object3D): string | null {
  const nodeId = resolveVisibleExportSourceAssemblyNodeId(object)
  if (nodeId) return nodeId
  if (object instanceof THREE.Mesh && isFeatureResultMesh(object)) {
    const geometryObjectId = object.objectId.trim()
    const occurrenceName = object.name.trim()
    return geometryObjectId
      ? `feature-result:${encodeURIComponent(geometryObjectId)}${occurrenceName && occurrenceName !== geometryObjectId ? `:${encodeURIComponent(occurrenceName)}` : ''}`
      : null
  }
  return null
}

/**
 * 为导出装配节点分配 ID。
 *
 * @param input 分配输入。
 * @returns 装配节点 ID 与兜底序号。
 */
function allocateAssemblyNodeId(input: {
  /** 导出构建上下文。 */
  context: PuppyVisibleModelBuildContext
  /** 源对象。 */
  object: THREE.Object3D
}): {
  /** 装配节点 ID。 */
  nodeId: string
  /** 用于兜底命名的序号。 */
  fallbackIndex: number
} {
  const stableNodeId = resolveStableSourceAssemblyNodeId(input.object)
  const fallbackIndex = input.context.assemblyNodeSeq + 1
  if (stableNodeId) {
    if (input.context.assemblyNodesById.has(stableNodeId)) {
      const existingNode = input.context.assemblyNodesById.get(stableNodeId)
      const sourceHierarchy = readVisibleExportSourceHierarchy(input.object)
      const ancestorPath: string[] = []
      let ancestor: THREE.Object3D | null = input.object
      while (ancestor) {
        const ancestorAssemblyNodeId = resolveStableSourceAssemblyNodeId(ancestor) ?? '-'
        const ancestorMetadata = ImportedModelObjectMetadataService.peekMetadata(ancestor)
        const ancestorPuppy = ancestor.userData?.puppy as { assemblyNodeId?: unknown } | undefined
        ancestorPath.push(
          `${ancestor.name || ancestor.type}[stable=${ancestorAssemblyNodeId};`
          + `runtimeWrapper=${ancestorMetadata?.runtimeWrapper === true};`
          + `hierarchy=${ancestorMetadata?.hierarchy?.name ?? '-'};`
          + `metadataAssembly=${ancestorMetadata?.assemblyNodeId ?? '-'};`
          + `targetAssembly=${ancestorMetadata?.selectionTarget?.assemblyNodeId ?? '-'};`
          + `puppyAssembly=${typeof ancestorPuppy?.assemblyNodeId === 'string' ? ancestorPuppy.assemblyNodeId : '-'};`
          + `children=${ancestor.children.length}]`,
        )
        ancestor = ancestor.parent
      }
      throw new Error(
        `PuppyCAD 可见模型存在重复稳定装配身份：assemblyNodeId=${stableNodeId}，`
        + `existing=${existingNode?.name ?? '-'}(${existingNode?.path ?? '-'})，`
        + `current=${input.object.name || input.object.type}(${sourceHierarchy?.path ?? '-'})，`
        + `ancestors=${ancestorPath.join(' <- ')}`,
      )
    }
    return {
      nodeId: stableNodeId,
      fallbackIndex,
    }
  }

  input.context.assemblyNodeSeq += 1
  return {
    nodeId: `assembly-node-${input.context.assemblyNodeSeq}`,
    fallbackIndex: input.context.assemblyNodeSeq,
  }
}

/**
 * 从 imported-model hierarchy 生成 assembly node 扩展字段。
 *
 * @param sourceHierarchy 源 imported-model hierarchy。
 * @returns 可写入 assembly node 的扩展字段。
 */
function createAssemblyNodeExtensions(
  sourceHierarchy: ReturnType<typeof readVisibleExportSourceHierarchy>,
): Record<string, unknown> | undefined {
  const selectionSourceObjectId = sourceHierarchy?.meta?.selectionObjectId?.trim()
  return selectionSourceObjectId
    ? { selectionSourceObjectId }
    : undefined
}

/**
 * 记录被折叠的 imported-model 语义节点，供后续 Mate 文档按节点身份重映射。
 *
 * @param input 折叠节点记录输入。
 */
/**
 * 创建离线包装配节点的可持久化 meta。
 *
 * @param sourceHierarchy 源 imported-model hierarchy。
 * @param nodeId 当前离线包装配节点 ID。
 * @returns 带稳定装配节点 ID 的 hierarchy meta。
 */
function createAssemblyNodeMeta(
  sourceHierarchy: ReturnType<typeof readVisibleExportSourceHierarchy>,
  nodeId: string,
  semanticKind?: PuppyAssemblyNode['semanticKind'],
): PuppyAssemblyNode['meta'] {
  const meta = cloneAssemblyNodeMeta(sourceHierarchy?.meta)
  return meta
    ? {
        ...meta,
        assemblyNodeId: nodeId,
      }
    : undefined
}

/**
 * 记录被折叠的 imported-model 语义节点，供后续 Mate 文档按节点身份重映射。
 *
 * @param input 折叠节点记录输入。
 */
function recordFoldedAssemblyNode(input: {
  context: PuppyVisibleModelBuildContext
  object: THREE.Object3D
  parentAssemblyNodeId: string | null
}): void {
  const sourceHierarchy = readVisibleExportSourceHierarchy(input.object)
  const semanticKind = sourceHierarchy?.meta?.semanticKind
  if (semanticKind !== 'assembly' && semanticKind !== 'part') {
    return
  }

  const nodeId = resolveStableSourceAssemblyNodeId(input.object) ?? input.object.uuid
  if (input.context.foldedAssemblyNodes.some((node) => node.nodeId === nodeId)) {
    return
  }

  const parentNode = input.parentAssemblyNodeId
    ? input.context.assemblyNodesById.get(input.parentAssemblyNodeId)
    : undefined
  const fallbackIndex = input.context.assemblyNodeSeq + input.context.foldedAssemblyNodes.length + 1
  const name = resolveAssemblyNodeName(input.object, fallbackIndex, input.context)
  input.context.foldedAssemblyNodes.push({
    nodeId,
    parentNodeId: input.parentAssemblyNodeId,
    name,
    nodeType: sourceHierarchy.nodeType ?? input.object.type,
    path: sourceHierarchy.path ?? (parentNode ? `${parentNode.path}/${name}` : name),
    selectable: sourceHierarchy.selectable,
    semanticKind,
    sourceObjectId: input.object.uuid,
    transform: readObjectLocalTransform(input.object),
    meta: createAssemblyNodeMeta(sourceHierarchy, nodeId, semanticKind),
    displayNodeIds: [],
    bodyBindingIds: [],
    geometryObjectIds: [],
    extensions: createAssemblyNodeExtensions(sourceHierarchy),
  })
}

/**
 * 判断当前对象是否应该创建独立装配节点。
 *
 * @param input 判断输入。
 * @returns 是否创建独立装配节点。
 */
function shouldCreateAssemblyNode(input: {
  object: THREE.Object3D
  parentAssemblyNodeId: string | null
  context: PuppyVisibleModelBuildContext
}): boolean {
  if (input.parentAssemblyNodeId === null) {
    return true
  }
  if (isPartAssemblyNode(input.context, input.parentAssemblyNodeId)) {
    return false
  }
  if (!(input.object instanceof THREE.Mesh)) {
    return true
  }
  if (shouldBindVisibleExportMeshToParentAssemblyNode({
    object: input.object,
    parentAssemblyNodeId: input.parentAssemblyNodeId,
  })) {
    return false
  }

  // 身份与 primitive 归属已经由统一策略判定；不能再次用局部标记覆盖独立 occurrence 身份。
  return true
}

/**
 * 判断装配节点是否是 SOLIDWORKS 意义上的零件刚体。
 *
 * @param context 离线包导出上下文。
 * @param assemblyNodeId 装配节点 ID。
 * @returns 是否为零件节点。
 */
function isPartAssemblyNode(
  context: PuppyVisibleModelBuildContext,
  assemblyNodeId: string | null,
): boolean {
  if (!assemblyNodeId) {
    return false
  }

  const node = context.assemblyNodesById.get(assemblyNodeId)
  return node?.semanticKind === 'part' || node?.meta?.semanticKind === 'part'
}

/**
 * 判断当前子树是否应继续复用父零件节点作为刚体边界。
 *
 * @param context 离线包导出上下文。
 * @param parentAssemblyNodeId 父装配节点 ID。
 * @returns 是否应把后代显示实体挂回父零件。
 */
function shouldInheritParentPartAssemblyNode(
  context: PuppyVisibleModelBuildContext,
  parentAssemblyNodeId: string | null,
): parentAssemblyNodeId is string {
  return isPartAssemblyNode(context, parentAssemblyNodeId)
}

/**
 * 创建装配节点。
 *
 * @param input 创建输入。
 * @returns 装配节点 ID。
 */
function createAssemblyNode(input: {
  context: PuppyVisibleModelBuildContext
  object: THREE.Object3D
  parentAssemblyNodeId: string | null
}): string {
  const { nodeId, fallbackIndex } = allocateAssemblyNodeId({
    context: input.context,
    object: input.object,
  })
  const sourceHierarchy = readVisibleExportSourceHierarchy(input.object)
  const parentNode = input.parentAssemblyNodeId
    ? input.context.assemblyNodesById.get(input.parentAssemblyNodeId)
    : undefined
  const name = resolveAssemblyNodeName(input.object, fallbackIndex, input.context)
  const path = sourceHierarchy?.path
    ?? (parentNode ? `${parentNode.path}/${name}` : name)
  //------ 装配体引用复制保存 开始------
  const baseExtensions = createAssemblyNodeExtensions(sourceHierarchy) ?? {}
  const referenceCopyMetadata = resolveAssemblyReferenceCopyExportMetadata(input.object)
  const referenceCopyLineageMetadata = resolveAssemblyReferenceCopyLineageExportMetadata(input.object)
  const extensions = {
    ...baseExtensions,
    ...(referenceCopyLineageMetadata ? { referenceLineage: referenceCopyLineageMetadata } : {}),
    ...(referenceCopyMetadata ? { referenceCopy: referenceCopyMetadata } : {}),
  }
  //------ 装配体引用复制保存 结束------
  const node: PuppyAssemblyNode = {
    nodeId,
    parentNodeId: input.parentAssemblyNodeId,
    name,
    nodeType: sourceHierarchy?.nodeType ?? input.object.type,
    path,
    selectable: sourceHierarchy?.selectable ?? true,
    semanticKind: sourceHierarchy?.meta?.semanticKind ?? (input.object instanceof THREE.Mesh ? 'part' : 'assembly'),
    sourceObjectId: input.object.uuid,
    transform: readObjectLocalTransform(input.object),
    meta: createAssemblyNodeMeta(sourceHierarchy, nodeId),
    displayNodeIds: [],
    bodyBindingIds: [],
    geometryObjectIds: [],
    //------ 装配体引用复制保存 开始------
    extensions: Object.keys(extensions).length > 0 ? extensions : undefined,
    //------ 装配体引用复制保存 结束------
  }

  input.context.assemblyNodes.push(node)
  input.context.assemblyNodesById.set(nodeId, node)
  input.context.childIdsByNodeId[nodeId] = []
  if (input.parentAssemblyNodeId) {
    input.context.childIdsByNodeId[input.parentAssemblyNodeId]?.push(nodeId)
  }
  return nodeId
}

//------ 装配体引用复制保存 开始------
/**
 * 克隆引用复制节点的装配语义，不克隆 display.glb 子树。
 *
 * 设计说明：
 * 装配体复制是 reference occurrence：保存时只需要在 assembly.json 中新增 occurrence 节点，
 * 并记录它引用的源节点；复制体局部矩阵由节点 transform 统一保存，可见 mesh、拓扑和 BRep 继续由源节点复用。
 *
 * @param input 克隆输入。
 * @returns 装配节点结果。
 */
function cloneAssemblyReferenceCopyObject(input: {
  source: THREE.Object3D
  parentAssemblyNodeId: string | null
  context: PuppyVisibleModelBuildContext
}): PuppyCloneTreeResult {
  const assemblyNodeId = createAssemblyNode({
    context: input.context,
    object: input.source,
    parentAssemblyNodeId: input.parentAssemblyNodeId,
  })

  return {
    clones: [],
    rootAssemblyNodeIds: [assemblyNodeId],
  }
}

/**
 * 判断引用复制体是否应只导出轻量 occurrence。
 *
 * @param input 判定输入。
 * @returns 是否跳过 display 子树导出。
 */
function shouldExportReferenceCopyAsLightweightOccurrence(input: {
  source: THREE.Object3D
  context: PuppyVisibleModelBuildContext
  metadata: AssemblyReferenceCopyMetadata
}): boolean {
  const lineage = resolveAssemblyReferenceCopyLineageExportMetadata(input.source)
  const familyId = lineage?.referenceFamilyId ?? input.metadata.referenceFamilyId ?? ''
  if (!familyId) {
    return true
  }

  if (input.context.originReferenceFamilyIds.has(familyId)) {
    return true
  }

  if (input.context.exportedReferenceFamilyIds.has(familyId)) {
    return true
  }

  /**
   * 原始 occurrence 已不存在时，第一个复制体必须承担同族 display payload 代表体职责。
   * 这样保存后仍只保留一份几何，不会让 A1/A2 全部变成无法 materialize 的空引用。
   */
  input.context.exportedReferenceFamilyIds.add(familyId)
  return false
}
//------ 装配体引用复制保存 结束------

/**
 * 删除没有任何可导出子树的临时装配节点。
 *
 * @param context 构建上下文。
 * @param nodeId 装配节点 ID。
 */
function removeAssemblyNode(context: PuppyVisibleModelBuildContext, nodeId: string): void {
  const node = context.assemblyNodesById.get(nodeId)
  if (!node) {
    return
  }
  context.assemblyNodes = context.assemblyNodes.filter((item) => item.nodeId !== nodeId)
  context.assemblyNodesById.delete(nodeId)
  delete context.childIdsByNodeId[nodeId]
  if (node.parentNodeId) {
    context.childIdsByNodeId[node.parentNodeId] = (context.childIdsByNodeId[node.parentNodeId] ?? [])
      .filter((childId) => childId !== nodeId)
  }
}

/**
 * 将 display mesh 绑定挂到装配节点。
 *
 * @param input 绑定输入。
 */
function attachDisplayBindingToAssemblyNode(input: {
  context: PuppyVisibleModelBuildContext
  assemblyNodeId: string
  displayNodeId: string
  bodyBindings: PuppyDisplayNodeBodyBinding[]
}): void {
  const node = input.context.assemblyNodesById.get(input.assemblyNodeId)
  if (!node) {
    throw new Error(`离线包装配节点不存在，无法挂载 display 绑定: ${input.assemblyNodeId}`)
  }
  pushUniqueStrings(node.displayNodeIds, [input.displayNodeId])
  pushUniqueStrings(node.bodyBindingIds, input.bodyBindings.map((binding) => binding.bodyBindingId))
  pushUniqueStrings(node.geometryObjectIds, input.bodyBindings.map((binding) => binding.geometryObjectId))
}

/**
 * 创建源 body、装配 occurrence 与 BRep 几何对象的组合键。
 *
 * @param bodyBindingId 源 body 绑定 ID。
 * @param geometryObjectId BRep 几何对象 ID。
 * @param occurrenceNodeId 装配 occurrence ID。
 * @returns 可用于 Map 的稳定组合键。
 */
/**
 * 分配冲突时的导出级 bodyBindingId。
 *
 * 设计说明：同一个装配 occurrence 内如果出现同一 bodyBindingId 绑定到不同几何对象，
 * 说明导出输入存在身份冲突。这里保留几何后缀兜底，避免拓扑资产互相覆盖。
 *
 * @param input 分配输入。
 * @returns 当前几何对象可使用的导出 bodyBindingId。
 */

/**
 * 判断 bodyBindingId 是否需要写入装配 occurrence scope。
 *
 * @param binding display body 绑定。
 * @returns 是否需要追加 occurrence scope。
 */

/**
 * 解析当前 display body 在离线包中的导出 bodyBindingId。
 *
 * 设计说明：
 * 插入零部件可能复用来源模型的 bodyBindingId；同源装配复制还可能继续复用同一个 geometryObjectId。
 * 若只按 body 或 body+geometry 去重，刷新重载后的 display mesh 会失去唯一归属，只能命中根装配或共享节点。
 * 这里把 imported body 的交互身份提升为“源 bodyBindingId + 装配 occurrence”，
 * geometryObjectId 只继续作为拓扑资产身份参与冲突校验。
 *
 * @param input 解析输入。
 * @returns 导出 bodyBindingId。
 */
function resolveExportBodyBindingId(input: {
  binding: PuppyDisplayNodeBodyBinding
  occurrenceNodeId: string
}): string {
  return createImportedBodyOccurrenceBindingId(input.binding.bodyBindingId, input.occurrenceNodeId)
}

/**
 * 用导出 bodyBindingId 克隆 display body 绑定。
 *
 * @param binding 源 display body 绑定。
 * @param exportBodyBindingId 导出 bodyBindingId。
 * @returns 可写入离线包的 display body 绑定。
 */
function cloneDisplayBodyBindingForExport(
  binding: PuppyDisplayNodeBodyBinding,
  exportBodyBindingId: string,
): PuppyDisplayNodeBodyBinding {
  return binding.bodyBindingId === exportBodyBindingId
    ? binding
    : {
        ...binding,
        bodyBindingId: exportBodyBindingId,
      }
}

/**
 * 用导出 bodyBindingId 克隆 topology-runtime 资产。
 *
 * @param input 克隆输入。
 * @returns 可写入离线包的拓扑资产。
 */
function cloneTopologyAssetForExport(input: {
  asset: PuppyVisibleBodyTopologyAsset
  binding: PuppyDisplayNodeBodyBinding
}): PuppyVisibleBodyTopologyAsset {
  return {
    ...input.asset,
    bodyBindingId: input.binding.bodyBindingId,
    geometryObjectId: input.binding.geometryObjectId,
    meshData: {
      ...input.asset.meshData,
      metadata: {
        ...(input.asset.meshData.metadata ?? {}),
        objectId: input.binding.geometryObjectId,
        bodyBindingId: input.binding.bodyBindingId,
        bodyId: input.binding.bodyId,
      },
    },
  }
}

/**
 * 注册当前 mesh 的 display body 与 topology-runtime 成对导出身份。
 *
 * @param input 注册输入。
 * @returns 已重写导出 bodyBindingId 的 display body 绑定。
 */
function registerExportBodyTopologyBindings(input: {
  context: PuppyVisibleModelBuildContext
  bodyBindings: PuppyDisplayNodeBodyBinding[]
  bodyTopologyAssets: PuppyVisibleBodyTopologyAsset[]
  occurrenceNodeId: string
}): PuppyDisplayNodeBodyBinding[] {
  const assetByBodyTopologyKey = new Map(
    input.bodyTopologyAssets.map((asset) => [
      createPuppyBodyTopologyAssetKey(asset),
      asset,
    ]),
  )

  return input.bodyBindings.map((binding) => {
    const exportBodyBindingId = resolveExportBodyBindingId({
      binding,
      occurrenceNodeId: input.occurrenceNodeId,
    })
    const exportBinding = cloneDisplayBodyBindingForExport(binding, exportBodyBindingId)
    const sourceAsset = assetByBodyTopologyKey.get(createPuppyBodyTopologyAssetKey(binding))

    if (sourceAsset) {
      const exportAsset = cloneTopologyAssetForExport({
        asset: sourceAsset,
        binding: exportBinding,
      })
      const exportAssetKey = createPuppyBodyTopologyAssetKey(exportAsset)
      const registeredAsset = input.context.bodyTopologyAssetsByKey.get(exportAssetKey)
      if (!registeredAsset) {
        input.context.bodyTopologyAssetsByKey.set(exportAssetKey, exportAsset)
      } else if (registeredAsset.geometryObjectId !== exportAsset.geometryObjectId) {
        throw new Error(
          `离线包导出 bodyBindingId 冲突: ${exportBodyBindingId} -> ${registeredAsset.geometryObjectId}/${exportAsset.geometryObjectId}`,
        )
      }
    }

    return exportBinding
  })
}

/**
 * 处理 mesh 的 BRep 与拓扑绑定。
 *
 * @param input 处理输入。
 * @returns body 绑定列表。
 */
function resolveExportableMeshBindings(input: {
  context: PuppyVisibleModelBuildContext
  mesh: THREE.Mesh
  occurrenceNodeId: string
}): PuppyDisplayNodeBodyBinding[] {
  const bindingResult = resolveDisplayNodeBodyBindings(input.mesh, input.context)
  if (bindingResult.bodyBindings.length === 0) {
    input.context.diagnostics.missingBrepBindingMeshCount += 1
  }
  if (bindingResult.ambiguous) {
    input.context.diagnostics.ambiguousBrepBindingMeshCount += 1
  }

  const bodyTopologyAssets = resolveVisibleBodyTopologyAssets(input.mesh, bindingResult.bodyBindings)
  const bodyBindings = registerExportBodyTopologyBindings({
    context: input.context,
    bodyBindings: bindingResult.bodyBindings,
    bodyTopologyAssets,
    occurrenceNodeId: input.occurrenceNodeId,
  })
  if (
    bindingResult.bodyBindings.length > 0
    && bodyTopologyAssets.length < bindingResult.bodyBindings.length
  ) {
    input.context.diagnostics.missingTopologySnapshotMeshCount += 1
  }

  return bodyBindings
}

/**
 * 将被剔除父层级的局部矩阵折叠到当前导出快照。
 *
 * 设计说明：
 * 1. 删除空承载层后，子级会直接挂到祖父级导出节点下；
 * 2. 如果不把承载层矩阵预乘到子级局部矩阵，GLB 中的世界位置会发生漂移；
 * 3. 同时写回 position/quaternion/scale，兼容 GLTFExporter 对 TRS 的读取。
 *
 * @param target 当前导出快照对象。
 * @param foldedAncestorMatrix 已剔除层级累积得到的局部矩阵。
 */
function applyFoldedAncestorMatrix(
  target: THREE.Object3D,
  foldedAncestorMatrix: THREE.Matrix4 | null,
): void {
  if (!foldedAncestorMatrix) {
    return
  }

  const foldedLocalMatrix = new THREE.Matrix4().multiplyMatrices(foldedAncestorMatrix, target.matrix)
  target.matrix.copy(foldedLocalMatrix)
  target.matrix.decompose(target.position, target.quaternion, target.scale)
}

/**
 * 计算继续向子级传递的折叠矩阵。
 *
 * @param input 矩阵计算输入。
 * @returns 包含当前被剔除层级的累计局部矩阵。
 */
function createNextFoldedAncestorMatrix(input: {
  /** 已剔除祖先层级的累计矩阵。 */
  foldedAncestorMatrix: THREE.Matrix4 | null
  /** 当前将被剔除的源 Object3D。 */
  source: THREE.Object3D
}): THREE.Matrix4 {
  input.source.updateMatrix()
  return input.foldedAncestorMatrix
    ? new THREE.Matrix4().multiplyMatrices(input.foldedAncestorMatrix, input.source.matrix)
    : input.source.matrix.clone()
}

/**
 * 跳过当前对象并把当前对象的本地变换折叠到可导出子级。
 *
 * @param input 当前对象克隆输入。
 * @returns 子级克隆结果；没有可导出子级时返回 null。
 */
function cloneVisibleObjectChildrenWithFoldedSource(input: {
  source: THREE.Object3D
  parentAssemblyNodeId: string | null
  context: PuppyVisibleModelBuildContext
  foldedAncestorMatrix: THREE.Matrix4 | null
}): PuppyCloneTreeResult | null {
  const nextFoldedAncestorMatrix = createNextFoldedAncestorMatrix({
    foldedAncestorMatrix: input.foldedAncestorMatrix,
    source: input.source,
  })
  const childResults = input.source.children
    .map((child) => cloneVisibleObjectTree({
      source: child,
      parentAssemblyNodeId: input.parentAssemblyNodeId,
      context: input.context,
      foldedAncestorMatrix: nextFoldedAncestorMatrix,
    }))
    .filter((result): result is PuppyCloneTreeResult => result !== null)

  return childResults.length > 0
    ? {
        clones: childResults.flatMap((result) => result.clones),
        rootAssemblyNodeIds: childResults.flatMap((result) => result.rootAssemblyNodeIds),
      }
    : null
}

/**
 * 克隆可导出的 mesh。
 *
 * @param input 克隆输入。
 * @returns 克隆结果。
 */
function cloneVisibleMesh(input: {
  source: THREE.Mesh
  parentAssemblyNodeId: string | null
  context: PuppyVisibleModelBuildContext
  foldedAncestorMatrix: THREE.Matrix4 | null
}): PuppyCloneTreeResult | null {
  if (!hasVisibleMaterial(input.source.material)) {
    input.context.diagnostics.skippedInvisibleMeshCount += 1
    return null
  }
  if (!hasRenderableGeometry(input.source)) {
    input.context.diagnostics.skippedEmptyMeshCount += 1
    return null
  }

  const ownsAssemblyNode = shouldCreateAssemblyNode({
    object: input.source,
    parentAssemblyNodeId: input.parentAssemblyNodeId,
    context: input.context,
  })
  const assemblyNodeId = ownsAssemblyNode
    ? createAssemblyNode({
        context: input.context,
        object: input.source,
        parentAssemblyNodeId: input.parentAssemblyNodeId,
      })
    : input.parentAssemblyNodeId

  if (!assemblyNodeId) {
    throw new Error(`离线包 mesh 缺少装配父节点: ${input.source.name || input.source.uuid}`)
  }

  const bodyBindings = resolveExportableMeshBindings({
    context: input.context,
    mesh: input.source,
    occurrenceNodeId: assemblyNodeId,
  })
  const fallbackDisplayNodeId = `display-node-${input.context.displayNodeSeq + 1}`
  const displayNodeId = allocateStableDisplayNodeId({
    usedDisplayNodeIds: input.context.displayNodeIds,
    source: input.source,
    assemblyNodeId,
    bodyBindings,
    fallbackDisplayNodeId,
  })
  if (displayNodeId === fallbackDisplayNodeId) {
    input.context.displayNodeSeq += 1
  }
  const snapshotMesh = createDisplaySnapshotMesh({
    source: input.source,
    displayNodeId,
    assemblyNodeId,
    ownsAssemblyNode,
  })
  applyFoldedAncestorMatrix(snapshotMesh, input.foldedAncestorMatrix)

  attachDisplayBindingToAssemblyNode({
    context: input.context,
    assemblyNodeId,
    displayNodeId,
    bodyBindings,
  })
  input.context.nodeBindings.push(createDisplayNodeBinding({
    displayNodeId,
    assemblyNodeId,
    nodeName: snapshotMesh.name,
    bodyBindings,
  }))
  input.context.diagnostics.exportedMeshCount += 1

  return {
    clones: [snapshotMesh],
    rootAssemblyNodeIds: [assemblyNodeId],
  }
}

/**
 * 克隆可导出的 Object3D 子树。
 *
 * @param input 克隆输入。
 * @returns 克隆结果；不可导出时返回 null。
 */
function cloneVisibleObjectTree(input: {
  source: THREE.Object3D
  parentAssemblyNodeId: string | null
  context: PuppyVisibleModelBuildContext
  foldedAncestorMatrix: THREE.Matrix4 | null
}): PuppyCloneTreeResult | null {
  if (!isWorldVisible(input.source)) {
    if (input.source instanceof THREE.Mesh) {
      input.context.diagnostics.skippedInvisibleMeshCount += 1
    }
    return null
  }
  if (shouldSkipVisibleExportSubtree(input.source)) {
    input.context.diagnostics.skippedNonModelObjectCount += 1
    return null
  }
  if (shouldSkipVisibleExportObjectOnly(input.source)) {
    input.context.diagnostics.skippedNonModelObjectCount += 1
    return cloneVisibleObjectChildrenWithFoldedSource(input)
  }
  recordSourceObjectLocalTransform(input.context, input.source)
  //------ 装配体引用复制保存 开始------
  const referenceCopyMetadata = AssemblyReferenceCopyMetadataService.readOwnMetadata(input.source)
  if (referenceCopyMetadata) {
    const shouldExportLightweightReferenceCopy = shouldExportReferenceCopyAsLightweightOccurrence({
      source: input.source,
      context: input.context,
      metadata: referenceCopyMetadata,
    })
    if (shouldExportLightweightReferenceCopy) {
      return cloneAssemblyReferenceCopyObject({
        source: input.source,
        parentAssemblyNodeId: input.parentAssemblyNodeId,
        context: input.context,
      })
    }
  }
  //------ 装配体引用复制保存 结束------
  if (input.source instanceof THREE.Mesh) {
    return cloneVisibleMesh({
      source: input.source,
      parentAssemblyNodeId: input.parentAssemblyNodeId,
      context: input.context,
      foldedAncestorMatrix: input.foldedAncestorMatrix,
    })
  }

  if (shouldFlattenVisibleExportAssemblyLayer({
    object: input.source,
    parentAssemblyNodeId: input.parentAssemblyNodeId,
  })) {
    recordFoldedAssemblyNode({
      context: input.context,
      object: input.source,
      parentAssemblyNodeId: input.parentAssemblyNodeId,
    })
    return cloneVisibleObjectChildrenWithFoldedSource(input)
  }

  if (shouldInheritParentPartAssemblyNode(input.context, input.parentAssemblyNodeId)) {
    const group = createDisplaySnapshotGroup({
      source: input.source,
      assemblyNodeId: input.parentAssemblyNodeId,
    })
    applyFoldedAncestorMatrix(group, input.foldedAncestorMatrix)

    input.source.children.forEach((child) => {
      const childResult = cloneVisibleObjectTree({
        source: child,
        parentAssemblyNodeId: input.parentAssemblyNodeId,
        context: input.context,
        //------ 装配体引用复制保存 开始------
        foldedAncestorMatrix: resolveReferenceCopyRepresentativeChildFoldedMatrix({
          context: input.context,
          metadata: referenceCopyMetadata,
          copiedObject: input.source,
          child,
        }),
        //------ 装配体引用复制保存 结束------
      })
      if (childResult) {
        childResult.clones.forEach((clone) => {
          group.add(clone)
        })
      }
    })

    return group.children.length > 0
      ? {
          clones: [group],
          rootAssemblyNodeIds: [input.parentAssemblyNodeId],
        }
      : null
  }

  const assemblyNodeId = createAssemblyNode({
    context: input.context,
    object: input.source,
    parentAssemblyNodeId: input.parentAssemblyNodeId,
  })
  const group = createDisplaySnapshotGroup({
    source: input.source,
    assemblyNodeId,
  })
  applyFoldedAncestorMatrix(group, input.foldedAncestorMatrix)

  input.source.children.forEach((child) => {
    const childResult = cloneVisibleObjectTree({
      source: child,
      parentAssemblyNodeId: assemblyNodeId,
      context: input.context,
      //------ 装配体引用复制保存 开始------
      foldedAncestorMatrix: resolveReferenceCopyRepresentativeChildFoldedMatrix({
        context: input.context,
        metadata: referenceCopyMetadata,
        copiedObject: input.source,
        child,
      }),
      //------ 装配体引用复制保存 结束------
    })
    if (childResult) {
      childResult.clones.forEach((clone) => {
        group.add(clone)
      })
    }
  })

  if (group.children.length === 0) {
    removeAssemblyNode(input.context, assemblyNodeId)
    return null
  }

  return {
    clones: [group],
    rootAssemblyNodeIds: [assemblyNodeId],
  }
}

/**
 * 构建只包含当前可见模型 mesh 的 GLB 导出根。
 *
 * @param sceneRoot 当前 Viewer 场景根。
 * @returns 临时导出根与节点绑定。
 */
export function createVisibleModelExportRoot(
  sceneRoot: THREE.Object3D,
  options: {
    /** 当前装配体标准配合文档 JSON。*/
    readonly mates?: AssemblyMateDocumentJSON
    /** 当前文档级基准数据。*/
    readonly datumExtension?: PuppyDatumExtensionInput | null
    /** 当前文档的权威装配语义源。 */
    readonly assemblySemanticSource?: PuppyExportAssemblySemanticSource | null
    /** 原生特征结果体 ID 到用户可见名称的映射。 */
    readonly resultBodyDisplayNameByObjectId?: PuppyExportResultBodyDisplayNameMap | null
    /** 当前离线包文档类型；只影响多顶层结果体的合成根语义。 */
    readonly documentType?: PuppyPackageDocumentType
  } = {},
): PuppyVisibleModelExportRoot {
  return importedModelSourceMeshExportBridge.readInstancedExportScene(
    sceneRoot,
    () => createVisibleModelExportRootFromBusinessScene(sceneRoot, options),
  )
}

/**
 * 在 occurrence 级 InstancedMesh 导出代理已挂载、业务 source Mesh 仍保持脱离的窗口内构建快照。
 *
 * @param sceneRoot 当前 Viewer 场景根。
 * @param options 可见模型导出选项。
 * @returns 临时导出根与节点绑定。
 */
function createVisibleModelExportRootFromBusinessScene(
  sceneRoot: THREE.Object3D,
  options: NonNullable<Parameters<typeof createVisibleModelExportRoot>[1]>,
): PuppyVisibleModelExportRoot {
  const root = new THREE.Group()
  const context = createBuildContext({
    resultBodyDisplayNameByObjectId: options.resultBodyDisplayNameByObjectId,
  })
  const sourceRoots = resolveVisibleExportSourceRoots(sceneRoot)
  context.originReferenceFamilyIds = collectOriginReferenceFamilyIds(sourceRoots)
  const topLevelNodeIds: string[] = []

  root.name = normalizeDisplayNodeName(sceneRoot.name || 'PuppyCAD Offline Package')
  sceneRoot.updateWorldMatrix(true, true)
  sourceRoots.forEach((source) => {
    const result = cloneVisibleObjectTree({
      source,
      parentAssemblyNodeId: null,
      context,
      foldedAncestorMatrix: null,
    })
    if (!result) {
      return
    }
    result.clones.forEach((clone) => {
      root.add(clone)
    })
    topLevelNodeIds.push(...result.rootAssemblyNodeIds)
  })

  const assemblyDocument = createPuppyAssemblyDocument({
    context,
    root,
    sceneRoot,
    topLevelNodeIds,
    mates: options.mates,
    datumExtension: options.datumExtension,
    documentType: options.documentType,
  })

  const exportRoot: PuppyVisibleModelExportRoot = {
    root,
    nodeBindings: context.nodeBindings,
    bodyTopologyAssets: Array.from(context.bodyTopologyAssetsByKey.values()),
    assemblyDocument,
    foldedAssemblyNodes: context.foldedAssemblyNodes,
    sourceObjectLocalTransforms: Object.fromEntries(context.sourceObjectLocalTransformById),
    diagnostics: context.diagnostics,
  }
  const remappedExportRoot = remapPuppyVisibleModelExportRootMateBodyBindingReferences(
    applyPuppyAssemblySemanticSource(exportRoot, options.assemblySemanticSource),
  )
  const finalizedAssembly = finalizePuppyAssemblyPersistence(
    remappedExportRoot.assemblyDocument,
    remappedExportRoot.runtimeSourceObjectIdsByAssemblyNodeId,
  )
  return {
    ...remappedExportRoot,
    assemblyDocument: finalizedAssembly.assembly,
    runtimeSourceObjectIdsByAssemblyNodeId: finalizedAssembly.runtimeSourceObjectIdsByAssemblyNodeId,
  }
}
